import { Box, ProgressCircle } from "@chakra-ui/react";
import { ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { RoutePath } from "./config";
import { consumeReturnTo, getSelectedInstanceId, rememberReturnTo } from "./authStore";

const Spinner = () => (
    <ProgressCircle.Root value={null} size="lg" transitionDuration={"200"} mt={"20vh"} color="orange" css={{ "--thickness": "2px" }} />
);

const ProtectedRoute = ({ children }: { children: ReactNode }): JSX.Element => {
    const auth = useAuth();
    const location = useLocation();
    const here = location.pathname + location.search;

    // Not signed in → remember where the user was going, then bounce to Keycloak (code+PKCE).
    // Keycloak returns to the site root, so without this a link to an order lands on the start page.
    useEffect(() => {
        if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator && !auth.error) {
            rememberReturnTo(here);
            void auth.signinRedirect();
        }
    }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.error]); // eslint-disable-line react-hooks/exhaustive-deps

    if (auth.isLoading || !auth.isAuthenticated) {
        return <Box transition={"all 0.5s ease"} flex={1} display="flex" flexDir="column"><Spinner /></Box>;
    }

    // Signed in but no instance chosen → pick one first (and come back here afterwards).
    if (getSelectedInstanceId() == null && location.pathname !== RoutePath.SelectInstance) {
        rememberReturnTo(here);
        return <Navigate to={RoutePath.SelectInstance} replace />;
    }

    // Signed in with a company: finish the trip to the page the user originally asked for.
    if (location.pathname !== RoutePath.SelectInstance) {
        const returnTo = consumeReturnTo();
        if (returnTo && returnTo !== here) {
            return <Navigate to={returnTo} replace />;
        }
    }

    return (
        <Box transition={"all 0.5s ease"} flex={1} display="flex" flexDir="column">
            {children}
        </Box>
    );
};

export default ProtectedRoute;
